import { useEffect, useState } from "react";
import axios from "axios";
import Chart from "react-apexcharts";
import { GraduationCapIcon, Loader2, Phone, University } from "lucide-react";
import API_URL from "../../config";
import { useNavigate } from "react-router-dom";

const Leads = () => {
  const [data, setData] = useState(null);
  const [type, setType] = useState("week");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // 🔥 FETCH DATA
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/api/admin/leads-stats?type=${type}`,
        );
        setData(res.data);
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [type]);

  const handleNavigate = (tab) => {
    if (tab === "ambassador") {
      navigate("/ambassador");
    } else if (tab === "priceQuery") {
      navigate("/price-query");
    } else {
      navigate("/lead-list"); // contact
    }
  };

  // 🔥 FORMAT CHART DATA
  const formatChartData = () => {
    if (!data) return { categories: [], series: [] };

    const price = data?.stats?.priceQuery || [];
    const contact = data?.stats?.contact || [];
    const ambassador = data?.stats?.ambassador || [];

    const map = {};

    const addData = (arr, key) => {
      arr.forEach((item) => {
        const label =
          type === "week"
            ? `${item._id.year}-W${item._id.week}`
            : `${item._id.year}-${new Date(
                0,
                item._id.month - 1,
              ).toLocaleString("default", { month: "short" })}`;

        if (!map[label]) {
          map[label] = {
            priceQuery: 0,
            contact: 0,
            ambassador: 0,
          };
        }

        map[label][key] = item.count;
      });
    };

    addData(price, "priceQuery");
    addData(contact, "contact");
    addData(ambassador, "ambassador");

    const sortedKeys = Object.keys(map).sort((a, b) => a.localeCompare(b));

    return {
      categories: sortedKeys,
      series: [
        {
          name: "Price Queries",
          data: sortedKeys.map((k) => map[k].priceQuery),
        },
        {
          name: "Contacts",
          data: sortedKeys.map((k) => map[k].contact),
        },
        {
          name: "Ambassador",
          data: sortedKeys.map((k) => map[k].ambassador),
        },
      ],
    };
  };

  const chartData = formatChartData();

  // 🔥 CHART OPTIONS
  const options = {
    chart: {
      type: "line",
      toolbar: { show: false },
    },
    xaxis: {
      categories: chartData.categories,
      labels: { style: { colors: "#000" } },
    },
    yaxis: {
      labels: { style: { colors: "#000" } },
    },
    stroke: { curve: "smooth" },
    tooltip: {
      intersect: false,
      shared: true,
      theme: "dark",
    },
    legend: { position: "bottom" },
  };

  // 🔥 LOADING
  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100 bg-dark">
        <Loader2 className="spinner-icon" size={60} color="#3b82f6" />
        <p className="mt-3 text-secondary fw-bold">Loading Stats...</p>

        <style jsx>{`
          .spinner-icon {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  }

  // 🔥 CARDS DATA
  const mapData = [
    {
      title: "Contact",
      value: data?.totals?.contact || 0,
      to: "contact",
      icon: <Phone size={18} />,
    },
    {
      title: "Price Query",
      value: data?.totals?.priceQuery || 0,
      to: "priceQuery",
      icon: <University size={18} />,
    },
    {
      title: "Ambassador",
      value: data?.totals?.ambassador || 0,
      to: "ambassador",
      icon: <GraduationCapIcon size={18} />,
    },
  ];

  return (
    <section className="d-flex flex-column align-items-center p-3 text-white">
      {/* 🔥 TOP CARDS */}
      <header className="row g-3 w-100 justify-content-center mb-4">
        {mapData.map((item, i) => (
          <div
            key={i}
            className="col-lg-3 col-md-4 col-6"
            onClick={() => handleNavigate(item.to)}
            style={{ cursor: "pointer" }}
          >
            <div
              className="card text-black h-100 p-3 border-0"
              style={{ background: "#fff" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  {item.icon}
                  <span className="fw-semibold">{item.title}</span>
                </div>
                <h4 className="text-black">{item.value}</h4>
              </div>
            </div>
          </div>
        ))}
      </header>

      {/* 🔥 GRAPH */}
      <main
        className="w-100 card p-3 border-0"
        style={{ background: "#fff", color: "#000" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="text-black">Leads Overview</h5>

          <div>
            <button
              className="btn me-2"
              style={{
                background: type === "week" ? "#6600cc" : "#006600",
                color: "#fff",
              }}
              onClick={() => setType("week")}
            >
              Week
            </button>

            <button
              className="btn"
              style={{
                background: type === "month" ? "#6600cc" : "#006600",
                color: "#fff",
              }}
              onClick={() => setType("month")}
            >
              Month
            </button>
          </div>
        </div>

        <Chart
          options={options}
          series={chartData.series}
          type={type === "week" ? "line" : "bar"}
          height={320}
        />
      </main>
    </section>
  );
};

export default Leads;
